import { forwardRef, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from '../icons/Icon';

type Variant = 'primary' | 'blue' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'lg';
  block?: boolean;
  icon?: IconName;
  /** Tooltip text (CSS `data-tip`). */
  tip?: string;
}

/** `.btn` pill button. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, block, icon, tip, className, children, type = 'button', ...rest },
  ref,
) {
  const cls = ['btn', variant, size, block && 'block', className].filter(Boolean).join(' ');
  return (
    <button ref={ref} type={type} className={cls} data-tip={tip} {...rest}>
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  /** Accessible name (required: icon-only control). */
  label: string;
  tip?: string;
  tipPos?: 'right' | 'below' | 'left';
  glassy?: boolean;
  on?: boolean;
  iconSize?: 'sm' | 'xs' | 'lg';
}

/** `.icon-btn` round icon button. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, tip, tipPos, glassy, on, iconSize, className, type = 'button', ...rest },
  ref,
) {
  const cls = ['icon-btn', glassy && 'glassy', on && 'on', className].filter(Boolean).join(' ');
  return (
    <button ref={ref} type={type} className={cls} aria-label={label} data-tip={tip} data-tip-pos={tipPos} {...rest}>
      <Icon name={icon} size={iconSize} />
    </button>
  );
});

export type Tone = 'accent' | 'blue' | 'ok' | 'warn' | 'danger';

export function Tag({ tone, children, tip }: { tone?: Tone | ''; children: ReactNode; tip?: string }) {
  return (
    <span className={['tag', tone].filter(Boolean).join(' ')} data-tip={tip}>
      {children}
    </span>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}

/** `.switch` with role=switch. */
export function Switch({ checked, onChange, label, disabled }: SwitchProps) {
  return (
    <button
      type="button"
      className="switch"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: IconName;
}

export interface SegmentedProps<T extends string> {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  label: string;
  /** `tabs` renders role=tablist/tab with aria-selected; default is a pressed-button group. */
  mode?: 'group' | 'tabs';
}

/** `.seg` segmented control. */
export function Segmented<T extends string>({ value, options, onChange, label, mode = 'group' }: SegmentedProps<T>) {
  return (
    <div className="seg" role={mode === 'tabs' ? 'tablist' : 'group'} aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role={mode === 'tabs' ? 'tab' : undefined}
          aria-selected={mode === 'tabs' ? option.value === value : undefined}
          aria-pressed={mode === 'group' ? option.value === value : undefined}
          onClick={() => onChange(option.value)}
        >
          {option.icon ? <Icon name={option.icon} size="sm" /> : null}
          {option.label}
        </button>
      ))}
    </div>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: IconName;
  label?: string;
  hint?: ReactNode;
  locked?: boolean;
  error?: boolean;
}

/** `.field` pill input with optional `.label` and `.hint`. */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ icon, label, hint, locked, error, id, className, ...rest }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={className}>
      {label ? (
        <label className="label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <div className={['field', locked && 'locked', error && 'err'].filter(Boolean).join(' ')}>
        {icon ? <Icon name={icon} size="sm" style={{ color: 'var(--ink-3)' }} /> : null}
        <input ref={ref} id={inputId} readOnly={locked || rest.readOnly} aria-invalid={error || undefined} aria-describedby={hint ? `${inputId}-hint` : undefined} {...rest} />
      </div>
      {hint ? (
        <p className="hint" id={`${inputId}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});

/** `role="alert"` for an error that appears in answer to what the person just did, so screen readers say it at once. */
export function Notice({ tone, icon = 'info', role, children, className }: { tone?: 'warn' | 'ok' | 'danger' | 'neutral'; icon?: IconName; role?: 'alert' | 'status'; children: ReactNode; className?: string }) {
  return (
    <div className={['notice', tone, className].filter(Boolean).join(' ')} role={role}>
      <Icon name={icon} size="sm" />
      <span>{children}</span>
    </div>
  );
}

/** Animated mesh-gradient background (`.bg`). Render once per window. */
export function Background() {
  return (
    <div className="bg" aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </div>
  );
}
